import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import TokenService from './TokenService';
import { IAuthTokenResponse } from '../interfaces';
import { showGlobalToast } from '../utils/toastHelper.ts';

// Create an axios instance with base configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to attach the access token to the headers
axiosInstance.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (
      config.url !== '/auth/login' &&
      config.url !== '/auth/refresh' &&
      config.url !== '/auth/forgot_password' &&
      config.url !== '/auth/check_password_reset_token' &&
      config.url !== '/auth/reset_password'
    ) {
      return Promise.reject(new Error('No token available'));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle login endpoint separately
    if (originalRequest.url === '/auth/login') {
      return Promise.reject(error);
    }
    
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = TokenService.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post<IAuthTokenResponse>(
          `${API_BASE_URL}/auth/refresh`,
          {
            refresh_token: refreshToken,
          },
          {
            headers: {
              Authorization: `Bearer ${TokenService.getAccessToken()}`,
            },
          }
        );

        const { access_token, refresh_token } = response.data;
        TokenService.setTokens(
          access_token,
          refresh_token,
          response.data.token
        );

        axiosInstance.defaults.headers.common['Authorization'] =
          `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        TokenService.clearTokens();
        
        // Redirect to login if not on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Global error handling for different status codes
    if (error.response) {
      const { status, data } = error.response;
      let errorMessage = data?.detail || data?.error || 'An error occurred.';

      switch (status) {
        case 400:
          showGlobalToast('Error', errorMessage, 'error');
          console.error(`400 Error: ${errorMessage}`);
          break;

        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          showGlobalToast('Forbidden', errorMessage, 'warning');
          console.error(`403 Error: ${errorMessage}`);
          break;

        case 404:
          errorMessage = 'The requested resource could not be found.';
          showGlobalToast('Not Found', errorMessage, 'warning');
          console.error(`404 Error: ${errorMessage}`);
          break;

        case 500:
          errorMessage =
            'An internal server error occurred. Please try again later.';
          showGlobalToast('Server Error', errorMessage, 'error');
          console.error(`500 Error: ${errorMessage}`);
          break;

        default:
          showGlobalToast('Error', errorMessage, 'error');
          console.error(`${status} Error: ${errorMessage}`);
          break;
      }
    } else {
      // Handle network or unknown errors
      showGlobalToast(
        'Network Error',
        'A network error occurred. Please check your internet connection.',
        'error'
      );
      console.error('Network Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
